import { extractFieldFromTextBuilder, createRegexSOLITT } from '../genericHelpers';
import { HandlerResult, PasteToQueryBaseHelper } from './pasteToQueryBaseHelper';

const extractField = extractFieldFromTextBuilder();
const extractFieldSOLITT = extractFieldFromTextBuilder(createRegexSOLITT);
const ORIGIN_FIELD_EXPECTED_PARTS = {
  FULL: 4,
  PARTIAL: 3
};

interface DitaQueryData {
  fileName: string;
  fileTitle: string;
  loio: string;
  deliverable: string;
  cmsProject: string;
  sourceContact: string;
  submap: string;
  languagesTranslatorTeams?: string;
}

type SolittOriginFieldData = Pick<DitaQueryData, 'cmsProject' | 'deliverable' | 'submap'>;

export class PasteToDitaQueryHelper extends PasteToQueryBaseHelper<DitaQueryData> {
  constructor() {
    super([PasteToDitaQueryHelper.getFromSOLITTHandler, PasteToDitaQueryHelper.getFromXTMHandler]);
  }

  private static getFromSOLITTHandler(
    text: string,
    { data }: HandlerResult<DitaQueryData>
  ): HandlerResult<DitaQueryData> {
    data.fileTitle = extractFieldSOLITT('Description', text);
    data.sourceContact = extractFieldSOLITT('Contact Person', text);
    data.loio = extractFieldSOLITT('LOIO', text);
    data.languagesTranslatorTeams = extractFieldSOLITT('Target Languages-Translator Teams', text);

    const objectName = extractFieldSOLITT('Object Name', text);
    const objectType = extractFieldSOLITT('Object Type', text);
    data.fileName = `${objectName} ${objectType}`;

    const origin = extractFieldSOLITT('Origin', text);
    const { cmsProject, deliverable, submap } = PasteToDitaQueryHelper.parseOriginField(origin);

    data.cmsProject = cmsProject || '';
    data.deliverable = deliverable || '';
    data.submap = submap || '';

    const done = !!data.fileTitle && !!data.sourceContact && !!data.fileName;

    return { data, done };
  }

  private static getFromXTMHandler(
    text: string,
    { data }: HandlerResult<DitaQueryData>
  ): HandlerResult<DitaQueryData> {
    data.fileName = extractField('Filename', text);
    data.fileTitle = extractField('File Title', text);
    data.loio = extractField('LOIO', text);
    data.deliverable = extractField('Deliverable', text);
    data.cmsProject = extractField('CMS Project', text);
    data.sourceContact = extractField('Contact Person', text);
    data.submap = extractField('Submap', text);
    data.languagesTranslatorTeams = extractField('Target Languages-Translator Teams', text);

    return { data };
  }

  private static parseOriginField = (origin: string): Partial<SolittOriginFieldData> => {
    const originParts = origin.split('/');

    if (originParts.length > ORIGIN_FIELD_EXPECTED_PARTS.FULL) {
      const [cmsProject, ...rest] = originParts;
      const deliverable = rest.slice(0, rest.length - 2).join('/');
      const submap = rest[rest.length - 2];
      return { cmsProject, deliverable, submap };
    }

    if (originParts.length === ORIGIN_FIELD_EXPECTED_PARTS.FULL) {
      const [cmsProject, deliverable, submap] = originParts;
      return { cmsProject, deliverable, submap };
    }

    if (originParts.length === ORIGIN_FIELD_EXPECTED_PARTS.PARTIAL) {
      const [cmsProject, deliverable] = originParts;
      return { cmsProject, deliverable };
    }

    return {};
  };
}
